"use client";

import { useEffect, useMemo, useState } from "react";
import { useEcommerce } from "../EcommerceProvider/EcommerceProvider";
import "./style.css";

type LegalKey = "privacyPolicy" | "returnsPolicy" | "terms";

type LegalDocumentProps = {
  field: LegalKey;
  title?: string;
  fallbackText?: string;
  className?: string;
  asPlainText?: boolean;
};

function looksLikeHtml(value: string) {
  return /<\/?[a-z][\s\S]*>/i.test(value);
}

function stripHtmlTags(html: string): string {
  return html.replace(/<[^>]*>/g, "").trim();
}

export function LegalDocument({
  field,
  fallbackText = "Treść nie jest dostępna.",
  className,
  asPlainText = false,
}: LegalDocumentProps) {
  const ecommerce = useEcommerce();
  const [safeHtml, setSafeHtml] = useState<string>("");

  const raw = useMemo(() => {
    return ((ecommerce?.[field] as string) ?? "").trim();
  }, [ecommerce, field]);

  const isHtml = useMemo(() => {
    if (!raw) return false;
    if (asPlainText) return false;
    return looksLikeHtml(raw);
  }, [raw, asPlainText]);

  useEffect(() => {
    if (!raw || !isHtml) {
      setSafeHtml("");
      return;
    }

    let cancelled = false;

    (async () => {
      const createDOMPurify = (await import("dompurify")).default;
      const DOMPurify = createDOMPurify(window);

      const clean = DOMPurify.sanitize(raw, {
        ALLOWED_TAGS: [
          "h1",
          "h2",
          "h3",
          "h4",
          "h5",
          "h6",
          "p",
          "a",
          "ul",
          "ol",
          "li",
          "strong",
          "em",
          "br",
          "hr",
          "blockquote",
          "span",
        ],
        ALLOWED_ATTR: ["href", "target", "rel"],
      });

      const withSafeLinks = clean.replace(/<a\b([^>]*?)>/gi, (_m, attrs) => {
        const target = /target=/i.test(attrs) ? "" : ' target="_blank"';
        const rel = /rel=/i.test(attrs)
          ? ""
          : ' rel="noopener noreferrer nofollow"';
        return `<a${attrs}${target}${rel}>`;
      });

      if (!cancelled) {
        setSafeHtml(withSafeLinks);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [raw, isHtml]);

  const hasContent = useMemo(() => {
    if (!safeHtml) return false;
    return stripHtmlTags(safeHtml).length > 0;
  }, [safeHtml]);

  return (
    <section className={className}>
      {!raw ? (
        <p className="mt-4 text-sm opacity-80">{fallbackText}</p>
      ) : isHtml ? (
        hasContent ? (
          <div
            className="legal-content mt-6"
            dangerouslySetInnerHTML={{ __html: safeHtml }}
          />
        ) : (
          <p className="mt-4 flex justify-center items-center text-sm opacity-80">
            {fallbackText}
          </p>
        )
      ) : (
        <div className="legal-content mt-6 whitespace-pre-line">{raw}</div>
      )}
    </section>
  );
}
